var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import FormGroup from '@mui/material/FormGroup';
import { useTheme } from '@mui/material/styles';
import { ResetButton, FormContainer, FormActions, SubmitButton } from '@/components/layout/forms';
import { useFormSync } from '@/hooks/use-form-sync';
import { useSNMP, useUpdateSNMP } from '@/api/services/snmp';
import { StatusToggleButton, ProtocolButtons, LocationField, ContactField, V1V2CheckboxField, V3CheckboxField } from './snmp-service-form-controls';
import { ReadWriteCommunityField, ReadOnlyCommunityField } from './v1-v2-settings-controls';
import { V3Settings } from './v3-settings';
import { transformToRequestObject, transformToFormDataObject } from './transformers';
import { SNMP_SERVICE_TAB_ID } from '@/pages/settings/services/alerting-and-monitoring';
export function SNMPServiceForm(_a) {
    var _this = this;
    var data = _a.data, permissions = _a.permissions;
    var updateSNMP = useUpdateSNMP();
    var isFetching = useSNMP().isFetching;
    var t = useTranslation().t;
    var theme = useTheme();
    var methods = useForm({ mode: 'onTouched', defaultValues: data });
    var isEnabled = methods.watch('enabled');
    var isV1EnabledChecked = methods.watch('v1Enabled');
    var isV3EnabledChecked = methods.watch('v3Enabled');
    var helperTextStyle = {
        color: theme.palette.error.main,
        margin: 0,
        marginTop: -0.5,
        lineHeight: 1.5,
        fontWeight: 400,
    };
    var onSubmit = useCallback(function (formData) { return __awaiter(_this, void 0, void 0, function () {
        var transformedData, updatedData, transformedUpdatedDate;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    transformedData = transformToRequestObject(formData);
                    return [4 /*yield*/, updateSNMP(transformedData)];
                case 1:
                    updatedData = _a.sent();
                    if (updatedData) {
                        transformedUpdatedDate = transformToFormDataObject(updatedData.snmp);
                        methods.reset(transformedUpdatedDate);
                    }
                    return [2 /*return*/];
            }
        });
    }); }, [methods, updateSNMP]);
    useFormSync(methods.formState.defaultValues, data, methods.reset, t('pages.alerts_and_monitoring.tabs.snmp_service.update.header'), isFetching);
    return (_jsxs(FormContainer, __assign({ ariaLabelledBy: SNMP_SERVICE_TAB_ID, mode: "update", permissions: permissions, methods: methods, onSubmit: onSubmit }, { children: [_jsxs(Stack, __assign({ spacing: 2 }, { children: [_jsx(StatusToggleButton, {}), _jsx(ProtocolButtons, {}), _jsx(LocationField, {}), _jsx(ContactField, {}), _jsxs(FormGroup, { children: [_jsxs(Stack, __assign({ direction: "row", spacing: 2 }, { children: [_jsx(V1V2CheckboxField, {}), _jsx(V3CheckboxField, {})] })), isEnabled && !isV1EnabledChecked && !isV3EnabledChecked && (_jsx(Typography, __assign({ sx: helperTextStyle }, { children: t('pages.alerts_and_monitoring.tabs.snmp_service.form.enabled_status_warning') })))] }), isV1EnabledChecked && (_jsxs(_Fragment, { children: [_jsx(Typography, __assign({ component: "h3", marginY: 2, variant: "h3" }, { children: t('pages.alerts_and_monitoring.tabs.snmp_service.form.v1_form_subheading') })), _jsx(ReadOnlyCommunityField, {}), _jsx(ReadWriteCommunityField, {})] })), isV3EnabledChecked && _jsx(V3Settings, {})] })), _jsxs(FormActions, { children: [_jsx(ResetButton, {}), _jsx(SubmitButton, {})] })] })));
}
